import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const VendorFeedback = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all, pending, resolved
  const navigate = useNavigate();

  useEffect(() => {
    fetchFeedbacks();
  }, []);

  const fetchFeedbacks = async () => {
    try {
  const response = await fetch(`${process.env.REACT_APP_VENDOR_DOMAIN}/api/feedback`);
      const data = await response.json();
      
      if (response.ok) {
        setFeedbacks(data.feedbacks || []);
      } else {
        setError(data.message || 'Failed to fetch feedback');
      }
    } catch (err) {
      setError('Error fetching feedback');
    } finally {
      setLoading(false);
    }
  };

  const markAsResolved = async (feedbackId) => {
    try {
  const response = await fetch(`${process.env.REACT_APP_VENDOR_DOMAIN}/api/feedback/${feedbackId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'resolved' })
      });

      if (response.ok) {
        setFeedbacks(prev => 
          prev.map(feedback => 
            feedback.id === feedbackId 
              ? { ...feedback, status: 'resolved' }
              : feedback
          )
        );
      } else {
        alert('Failed to update feedback status');
      }
    } catch (err) {
      alert('Error updating feedback status');
    }
  };

  const deleteFeedback = async (feedbackId) => {
    if (!window.confirm('Are you sure you want to delete this feedback?')) {
      return;
    }

    try {
  const response = await fetch(`${process.env.REACT_APP_VENDOR_DOMAIN}/api/feedback/${feedbackId}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        setFeedbacks(prev => prev.filter(feedback => feedback.id !== feedbackId));
      } else {
        alert('Failed to delete feedback');
      }
    } catch (err) {
      alert('Error deleting feedback');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#ff9800';
      case 'resolved': return '#388e3c';
      default: return '#666';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return '#d32f2f';
      case 'medium': return '#ff9800';
      case 'low': return '#388e3c';
      default: return '#666';
    }
  };

  const filteredFeedbacks = feedbacks.filter(feedback => {
    if (filter === 'all') return true;
    return feedback.status === filter;
  });

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading feedback...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h2 style={styles.title}>Customer Feedback Management</h2>
        <button
          style={styles.backBtn}
          onClick={() => navigate('/admin')}
        >
          ← Back to Admin Dashboard
        </button>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      {/* Filter Controls */}
      <div style={styles.filterContainer}>
        <label style={styles.filterLabel}>Filter by Status:</label>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          style={styles.filterSelect}
        >
          <option value="all">All Feedback</option>
          <option value="pending">Pending</option>
          <option value="resolved">Resolved</option>
        </select>
        <span style={styles.count}>
          Showing {filteredFeedbacks.length} of {feedbacks.length} feedback entries
        </span>
      </div>

      {filteredFeedbacks.length === 0 ? (
        <div style={styles.empty}>
          {filter === 'all' ? 'No feedback found.' : `No ${filter} feedback found.`}
        </div>
      ) : (
        <div style={styles.feedbackGrid}>
          {filteredFeedbacks.map(feedback => (
            <div key={feedback.id} style={styles.feedbackCard}>
              <div style={styles.cardHeader}>
                <div style={styles.cardTitle}>
                  <strong>#{feedback.id}</strong> 
                </div>
                <div style={styles.cardMeta}>
                  <span 
                    style={{
                      ...styles.statusBadge,
                      backgroundColor: getStatusColor(feedback.status),
                    }}
                  >
                    {feedback.status || 'pending'}
                  </span>
                  {feedback.priority && (
                    <span 
                      style={{
                        ...styles.priorityBadge,
                        backgroundColor: getPriorityColor(feedback.priority),
                      }}
                    >
                      {feedback.priority} priority
                    </span>
                  )}
                </div>
              </div>

              <div style={styles.cardContent}>
                {feedback.email && (
                  <div style={styles.infoRow}>
                    <strong>Email:</strong> {feedback.email}
                  </div>
                )}
                
                {feedback.subject && (
                  <div style={styles.infoRow}>
                    <strong>Subject:</strong> {feedback.subject}
                  </div>
                )}

                <div style={styles.infoRow}>
                  <strong>Message:</strong>
                  <div style={styles.message}>
                    {feedback.feedback || 'No message provided'}
                  </div>
                </div>

                {feedback.category && (
                  <div style={styles.infoRow}>
                    <strong>Category:</strong> {feedback.category}
                  </div>
                )}

                <div style={styles.infoRow}>
                  <strong>Submitted:</strong> {formatDate(feedback.creation_date)}
                </div>

                {feedback.user_id && (
                  <div style={styles.infoRow}>
                    <strong>User ID:</strong> {feedback.user_id}
                  </div>
                )}
              </div>

              <div style={styles.cardActions}>
                {feedback.status !== 'resolved' && (
                  <button
                    style={styles.resolveBtn}
                    onClick={() => markAsResolved(feedback.id)}
                  >
                    Mark as Resolved
                  </button>
                )}
                
                <button
                  style={styles.deleteBtn}
                  onClick={() => deleteFeedback(feedback.id)}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '20px',
    fontFamily: 'Arial, sans-serif',
    background: '#e3f2fd', // Light blue background
    minHeight: '100vh',
    borderRadius: '8px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '30px',
    flexWrap: 'wrap',
    gap: '15px',
  },
  title: {
    color: '#1976d2', // Darker blue for better contrast
    fontSize: '2rem',
    fontWeight: 'bold',
    margin: 0,
  },
  backBtn: {
    background: '#1976d2', // Darker blue for better contrast
    color: '#fff',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 20px',
    fontSize: '1rem',
    cursor: 'pointer',
    fontWeight: 'bold',
    textDecoration: 'none',
  },
  loading: {
    textAlign: 'center',
    color: '#666',
    fontSize: '1.2rem',
    marginTop: '50px',
  },
  error: {
    background: '#ffebee',
    color: '#c62828',
    padding: '15px',
    borderRadius: '6px',
    marginBottom: '20px',
    border: '1px solid #ef5350',
  },
  filterContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    marginBottom: '25px',
    padding: '15px',
    background: '#f8f9fa', // Slightly different from main background
    borderRadius: '8px',
    flexWrap: 'wrap',
    border: '1px solid #bbdefb', // Light blue border
  },
  filterLabel: {
    fontWeight: 'bold',
    color: '#333',
  },
  filterSelect: {
    padding: '8px 12px',
    borderRadius: '4px',
    border: '1px solid #ccc',
    fontSize: '1rem',
  },
  count: {
    color: '#666',
    fontSize: '0.9rem',
    marginLeft: 'auto',
  },
  empty: {
    textAlign: 'center',
    color: '#666',
    fontSize: '1.2rem',
    padding: '60px 20px',
    background: '#fff',
    borderRadius: '8px',
    fontStyle: 'italic',
    border: '1px solid #bbdefb', // Light blue border
  },
  feedbackGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
    gap: '20px',
  },
  feedbackCard: {
    background: '#fff',
    border: '1px solid #bbdefb', // Light blue border
    borderRadius: '8px',
    padding: '20px',
    boxShadow: '0 2px 8px rgba(25, 118, 210, 0.1)', // Blue-tinted shadow
    transition: 'transform 0.2s, boxShadow 0.2s',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '15px',
    flexWrap: 'wrap',
    gap: '10px',
  },
  cardTitle: {
    fontSize: '1.1rem',
    color: '#1976d2', // Darker blue for better contrast
    fontWeight: 'bold',
  },
  cardMeta: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
  },
  statusBadge: {
    color: '#fff',
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
    textTransform: 'capitalize',
  },
  priorityBadge: {
    color: '#fff',
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
    textTransform: 'capitalize',
  },
  cardContent: {
    marginBottom: '20px',
  },
  infoRow: {
    marginBottom: '10px',
    fontSize: '0.95rem',
  },
  message: {
    background: '#f8f9fa',
    padding: '10px',
    borderRadius: '4px',
    marginTop: '5px',
    border: '1px solid #e9ecef',
    lineHeight: '1.4',
    whiteSpace: 'pre-wrap',
  },
  cardActions: {
    display: 'flex',
    gap: '10px',
    flexWrap: 'wrap',
  },
  resolveBtn: {
    background: '#388e3c',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    fontSize: '0.9rem',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  deleteBtn: {
    background: '#d32f2f',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    padding: '8px 16px',
    fontSize: '0.9rem',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
};

export default VendorFeedback;